<script setup>
import { ref, computed, defineAsyncComponent } from 'vue'
import { useStore } from 'vuex'
import { bbox } from '@turf/turf'
import { FolderDot, ChevronDown, Eye, EyeOff, ZoomIn, FileText, Image, Loader2, Download, Ellipsis, Pencil, Share, Trash2, Layers, Satellite } from 'lucide-vue-next'
import { VerifiedMarker } from '@/components/ui/verified-marker'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { useRouter } from 'vue-router'
import { useToast } from '@/components/ui/toast/use-toast'
import { Button } from '@/components/ui/button'
import { DialogConfirm } from '@/components/dialogs'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger
} from '@/components/ui/dropdown-menu'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible'
import { Badge } from '@/components/ui/badge'
import { Point, LineString, Polygon, MultiPolygon } from '@/components/icons'
import { MediaList } from '@/components/media'
import { API, HOST } from '@/utils/http'
import { formatTime, formatTimeFromNow } from '@/utils/datetime'

const props = defineProps({
  feature: Object
})

const FeaturesListChild = defineAsyncComponent(() => import("@/components/features/FeaturesList.vue"))

const isShowChilds = ref(false)
const isDateToggle = ref(true)
const currentFeatureChilds = ref(null)

const store = useStore()
const router = useRouter()
const { toast } = useToast()
const map = computed(() => store.getters['geo/getMap'])
const settings = computed(() => store.getters['user/getUserSettings'])
const currentFeature = computed(() => store.getters['geo/getCurrentFeature'])

const isCurrent = computed(() => {
  if (!currentFeature.value) return false
  return currentFeature.value.properties.code === props.feature.properties.code
})

const hasTemplate = computed(() => props.feature.properties.template_id != null)

const templateName = computed(() => {
  if (!hasTemplate.value) return null

  if (props.feature.properties.template_name) {
    return props.feature.properties.template_name
  }

  const allTemplates = store.getters['geo/getAllTemplates']
  if (allTemplates && allTemplates.length > 0) {
    const template = allTemplates.find(t => t.id === props.feature.properties.template_id)
    if (template) return template.name
  }
  const projectTemplates = store.getters['geo/getProjectReportTemplates']
  if (projectTemplates && projectTemplates.length > 0) {
    const template = projectTemplates.find(t => t.id === props.feature.properties.template_id)
    if (template) return template.name
  }
  return 'Template'
})

const isVisible = computed(() => {
  return store.getters['geo/getFeatureVisibility'](props.feature.properties.code)
})

const toggleVisibility = () => {
  store.commit('geo/TOGGLE_FEATURE_VISIBILITY', props.feature.properties.code)
}

const fitToGeometry = () => {
  if (!map.value || !props.feature) return

  // Use pre-computed center coords (feed features have no geometry)
  const { center_lat, center_lng } = props.feature.properties
  if (center_lat != null && center_lng != null && !props.feature.geometry) {
    map.value.flyTo({ center: [center_lng, center_lat], zoom: 14, duration: 500 })
    return
  }

  // Fallback to bbox for features with geometry (after heavy load)
  try {
    const featureBbox = bbox(props.feature)
    map.value.fitBounds(featureBbox, {
      padding: 100,
      duration: 500
    })
  } catch (error) {
    console.error('Error zooming to feature:', error)
    if (center_lat != null && center_lng != null) {
      map.value.flyTo({ center: [center_lng, center_lat], zoom: 14, duration: 500 })
    }
  }
}

const isHeavy = computed(() => props.feature.properties.is_heavy === true)
const isHeavyLoading = computed(() => store.getters['geo/isHeavyFeatureLoading'](props.feature.properties.code))
const isHeavyLoaded = computed(() => store.getters['geo/isHeavyFeatureLoaded'](props.feature.properties.code))

const loadHeavyGeometry = () => {
  store.dispatch('geo/loadHeavyFeatureGeometry', props.feature.properties.code)
}

const iconName = computed(() => {
  const subtype = props.feature.properties.subtype ? props.feature.properties.subtype : 'report'
  const geometryType = props.feature.properties.type?.toLowerCase() || 'polygon'
  return subtype === 'report' ? geometryType : subtype
})

const mediaCount = computed(() => {
  if (!props.feature.properties.resources || props.feature.properties.resources.length === 0) {
    return 0
  }

  // Group by UUID to count unique media items (not variants)
  const uniqueMedia = new Set()
  props.feature.properties.resources.forEach(resource => {
    if (resource.uuid) {
      uniqueMedia.add(resource.uuid)
    }
  })

  return uniqueMedia.size
})


const createdAt = computed(() => {
  let created_origin = props.feature.properties.created_origin
  if (!created_origin) return props.feature.properties.created_at
  if (created_origin.toString().length === 10) {
    created_origin = created_origin * 1000
  }
  return created_origin
})

const createdAtFormated = computed(() => formatTime(createdAt.value, settings.value.tz, 'D MMMM YYYY HH:mm'))
const createdAtFromNow = computed(() => formatTimeFromNow(createdAt.value, settings.value.tz))

const dialogDeleteConfirm = ref(null)
const isAddingToDataTab = ref(false)

const editFeature = () => {
  router.push({ name: 'FeatureEdit', params: { featureCode: props.feature.properties.code } })
}

const copyUrl = () => {
  const url = router.resolve({ name: 'Feature', params: { project_id: props.feature.properties.project_id, featureCode: props.feature.properties.code } }).href
  const fullUrl = window.location.origin + url
  navigator.clipboard.writeText(fullUrl)
  toast({ title: 'Share link copied to clipboard' })
}

const addToDataTab = async () => {
  if (isAddingToDataTab.value) return
  isAddingToDataTab.value = true
  try {
    const orgId = store.getters['org/getSelectedOrg'].organization_id
    await API({
      method: 'POST',
      url: `${HOST}/org/${orgId}/layers`,
      data: {
        org_id: parseInt(orgId),
        type: 5,
        name: props.feature.properties.name,
        layer: {
          data: {
            feature_ids: [props.feature.properties.code]
          }
        }
      }
    })
    toast({ title: 'Added to Data', description: `Static layer "${props.feature.properties.name}" created` })
  } catch (error) {
    console.error('Error creating static layer:', error)
    toast({ title: 'Error', description: error.response?.data?.detail || 'Could not add to Data', variant: 'destructive' })
  }
  isAddingToDataTab.value = false
}

const deleteFeature = () => {
  store.dispatch('geo/deleteFeature', props.feature.properties.code)
}

async function fetchFeatureChilds() {
  const response = await API({
    method: 'GET',
    url: `${HOST}/geo/feature/${props.feature.properties.code}/childs`
  })
  currentFeatureChilds.value = response.data
}
</script>

<template>
  <div class="border-b" :class="{'': isCurrent}">
    <div style="overflow: hidden;">
      <div class="px-3 py-3">
        <div class="flex items-center justify-between">
          <div class="flex items-center">
            <Avatar class="w-5 h-5 mr-2">
              <AvatarImage
                v-if="props.feature.properties.member_avatar_url"
                :src="props.feature.properties.member_avatar_url"
                :alt="props.feature.properties.member_display_name"
                :key="props.feature.properties.code"
              />
              <AvatarFallback />
            </Avatar>
            <span class="text-muted-foreground">{{ props.feature.properties.member_display_name }}</span>
            <VerifiedMarker v-if="props.feature.properties.member_verified" class="ml-1 flex-shrink-0" />
          </div>
          <div @click.stop="isDateToggle = !isDateToggle">
            <span v-if="isDateToggle" class="text-muted-foreground">{{ createdAtFromNow }}</span>
            <span v-else class="text-muted-foreground">{{ createdAtFormated }}</span>
          </div>
        </div>
        <div class="mt-3 flex">
          <div
            class="min-w-8 min-h-8 w-8 h-8 rounded-full shadow-sm flex items-center justify-center"
            :style="{'background-color': props.feature.properties.color}"
          >
            <div v-if="props.feature.properties.s3_icon">
              <img class="object-contain w-5 h-5" :src="props.feature.properties.s3_icon" alt="" />
            </div>
            <div v-else>
              <Point v-if="iconName === 'point'" class="fill-white" />
              <LineString v-if="iconName === 'linestring'" class="fill-white" />
              <Polygon v-if="iconName === 'polygon'" class="fill-white" />
              <MultiPolygon v-if="iconName === 'multipolygon'" class="fill-white" />
            </div>
          </div>
          <div class="ml-3 mt-1">
            <div class="flex items-center gap-1.5">
              <router-link :to="{ name: 'Feature', params: { featureCode: props.feature.properties.code, project_id: props.feature.properties.project_id } }">
                <h3 class="text-base font-semibold hover:text-primary">
                  <span>{{ props.feature.properties.name }}</span>
                </h3>
              </router-link>
              <Popover v-if="props.feature.properties.monitored">
                <PopoverTrigger as-child>
                  <button type="button" @click.stop>
                    <Satellite class="w-3.5 h-3.5 text-primary" />
                  </button>
                </PopoverTrigger>
                <PopoverContent class="w-64 text-sm" align="start">
                  <div class="font-medium">Monitored area</div>
                  <p class="mt-1 text-muted-foreground">Satellite monitoring is active for this area — new measurements and imagery arrive regularly.</p>
                </PopoverContent>
              </Popover>
            </div>
            <div v-if="hasTemplate && templateName" class="mt-1">
              <Badge variant="secondary" class="text-xs py-0 px-1.5">
                <FileText class="w-3 h-3 mr-1" />
                {{ templateName }}
              </Badge>
            </div>
          </div>
        </div>

        <!-- Media gallery -->
        <div v-if="mediaCount > 0" class="mt-3">
          <div class="flex items-center mb-2">
            <Image class="w-3.5 h-3.5 mr-1.5 text-muted-foreground" />
            <span class="text-sm text-muted-foreground">Media ({{ mediaCount }})</span>
          </div>
          <MediaList :resources="props.feature.properties.resources" />
        </div>

        <!-- Heavy feature load banner -->
        <div v-if="isHeavy && !isHeavyLoaded" class="mt-3 flex items-center justify-between rounded-md bg-yellow-50 dark:bg-yellow-950 border border-yellow-200 dark:border-yellow-800 px-3 py-2">
          <span class="text-sm text-yellow-800 dark:text-yellow-200">
            Heavy geometry
          </span>
          <Button
            @click.stop="loadHeavyGeometry"
            size="sm"
            variant="outline"
            class="ml-2 h-7 border-yellow-300 dark:border-yellow-700 text-yellow-800 dark:text-yellow-200 hover:bg-yellow-100 dark:hover:bg-yellow-900"
            :disabled="isHeavyLoading"
          >
            <Loader2 v-if="isHeavyLoading" class="h-3.5 w-3.5 mr-1 animate-spin" />
            <Download v-else class="h-3.5 w-3.5 mr-1" />
            {{ isHeavyLoading ? 'Loading...' : 'Load' }}
          </Button>
        </div>

        <div class="flex items-center justify-between mt-3">
          <div class="flex items-center justify-end">
            <Button
              @click.stop="fitToGeometry"
              variant="ghost"
              size="icon"
              class="h-6 w-6"
              title="Fit to geometry"
            >
              <ZoomIn class="h-4 w-4 text-muted-foreground" />
            </Button>
            <Button
              @click.stop="toggleVisibility"
              variant="ghost"
              size="icon"
              class="h-6 w-6"
              :title="isVisible ? 'Hide on map' : 'Show on map'"
            >
              <Eye v-if="isVisible" class="h-4 w-4 text-muted-foreground" />
              <EyeOff v-else class="h-4 w-4 text-muted-foreground" />
            </Button>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon" class="h-6 w-6" @click.stop>
                  <Ellipsis class="h-4 w-4 text-muted-foreground" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent class="w-48">
                <DropdownMenuGroup>
                  <DropdownMenuItem @click.stop="fitToGeometry" class="cursor-pointer">
                    <Eye class="w-4 h-4 mr-2" />
                    View on Map
                  </DropdownMenuItem>
                  <DropdownMenuItem @click.stop="editFeature" class="cursor-pointer">
                    <Pencil class="w-4 h-4 mr-2" />
                    Edit
                  </DropdownMenuItem>
                  <DropdownMenuItem @click.stop="addToDataTab" class="cursor-pointer" :disabled="isAddingToDataTab">
                    <Layers class="w-4 h-4 mr-2" />
                    {{ isAddingToDataTab ? 'Adding...' : 'Add to Data' }}
                  </DropdownMenuItem>
                  <DropdownMenuItem @click.stop="copyUrl" class="cursor-pointer">
                    <Share class="w-4 h-4 mr-2" />
                    Share
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DialogConfirm
                    ref="dialogDeleteConfirm"
                    @confirm="deleteFeature"
                    title="Delete report"
                    description="Are you sure you want to delete this report?"
                  >
                    <DropdownMenuItem class="cursor-pointer text-red-600 focus:text-red-600" @select.prevent>
                      <Trash2 class="w-4 h-4 mr-2" />
                      Delete
                    </DropdownMenuItem>
                  </DialogConfirm>
                </DropdownMenuGroup>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
          <div v-if="props.feature.properties.shared" class="flex items-center justify-between">
            <Badge variant="secondary" class="text-xs">Public</Badge>
          </div>
        </div>
      </div>
    </div>

    <div v-if="props.feature.properties.childs_count > 0" class="px-3 pb-3">
      <Collapsible v-model:open="isShowChilds">
        <CollapsibleTrigger>
          <Button size="sm" variant="outline" @click.stop="fetchFeatureChilds" class="flex items-center text-muted-foreground">
            {{ props.feature.properties.childs_count }} reports
            <ChevronDown class="ml-1 w-4 h-4 text-muted-foreground" />
          </Button>
        </CollapsibleTrigger>
        <CollapsibleContent>
          <div class="border border-border mt-2 rounded-lg">
            <FeaturesListChild
              v-if="currentFeatureChilds"
              :features="currentFeatureChilds?.features"
              is-child
              :key="props.feature.properties.code"
              no-pagination
            />
          </div>
        </CollapsibleContent>
      </Collapsible>
    </div>
  </div>
</template>
